﻿/**
 * A class which provides functions for displaying flash (temporary) messages on a web page.
 * @module core/base/Messenger
 */
import _ from "underscore";
import extend from "backbone-extend-standalone";
var INFO = "info",
  WARNING = "warning",
  DANGER = "danger",
  SUCCESS = "success",
  TYPES = [INFO, WARNING, DANGER, SUCCESS];

/**
 * Creates an instance of a messenger.
 * @constructor
 */
var Messenger = function () {};

Messenger.prototype = {
  /**
   * Displays an informational message.
   * @param  {string} message The message.
   * @param  {Boolean} [keep]    True to keep the message from disappearing after the delay.
   * @param  {Boolean} [append]  True to add the message after other messages, as opposed to replacing them.
   * @param {Integer} [delay] The delay, in milliseconds, between displaying the message and destroying it.
   * @return {jquery}         The element containing the flash message.
   */
  info: function (message, keep, append, delay) {
    delay = delay || 500;

    return this.doUpdate(INFO, message, keep, append, delay);
  },

  /**
   * Displays a warning message.
   * @param  {string} message The message.
   * @param  {Boolean} [keep]    True to keep the message from disappearing after the delay.
   * @param  {Boolean} [append]  True to add the message after other messages, as opposed to replacing them.
   * @param {Integer} [delay] The delay, in milliseconds, between displaying the message and destroying it.
   * @return {jquery}         The element containing the flash message.
   */
  warning: function (message, keep, append, delay) {
    delay = delay || 2500;

    return this.doUpdate(WARNING, message, keep, append, delay);
  },

  /**
   * Displays an error message.
   * @param  {string} message The message.
   * @param  {Boolean} [keep]    True to keep the message from disappearing after the delay.
   * @param  {Boolean} [append]  True to add the message after other messages, as opposed to replacing them.
   * @param {Integer} [delay] The delay, in milliseconds, between displaying the message and destroying it.
   * @return {jquery}         The element containing the flash message.
   */
  error: function (message, keep, append, delay) {
    delay = delay || 5000;

    return this.doUpdate(DANGER, message, keep, append, delay);
  },

  /**
   * Displays a success message.
   * @param  {string} message The message.
   * @param  {Boolean} [keep]    True to keep the message from disappearing after the delay.
   * @param  {Boolean} [append]  True to add the message after other messages, as opposed to replacing them.
   * @return {jquery}         The element containing the flash message.
   */
  success: function (message, keep, append) {
    return this.doUpdate(SUCCESS, message, keep, append);
  },

  /**
   * Displays messages from a JSON object.
   *
   * @example
   * require(['app/Flash'], function(Flash){
   *     var flash = new Flash($('<div></div>'));
   *     flash.post({info: 'Informational message'});
   *     // equivalent to flash.info('Informational message');
   * });
   * @param  {object} data   A mapping containing the messages to display.
   * @param  {Boolean} [keep]   True to keep the messages from disappearing after the delay.
   * @param  {Boolean} [append] True to add the messages after each other, as opposed to replacing each previous message.
   */
  post: function (data, keep, append) {
    _.each(
      TYPES,
      function (type) {
        if (data[type]) {
          this[type](data[type], keep, append);
        }
      },
      this
    );
  },

  /**
   * Asynchronously removes messages.
   * @param  {Boolean} [all:false] True to expire all messages (including errors), otherwise false.
   * @abstract
   */
  reset: function (all) {},

  /**
   * Publish a message to the user.
   * @param {string} type One of Messenger.TYPES
   * @param {string} message The message to publish.
   * @param {Boolean} [keep:false] True to keep the message around when additional messages are published.
   * @param {Boolean} [append:false] True to append to the existing list of messages, otherwise false.
   * @param {Integer} [delay:1000] The number of milliseconds to persist the message.
   * @protected
   * @abstract
   */
  doUpdate: function (type, message, keep, append, delay) {},
};

_.extend(Messenger, {
  INFO: INFO,
  SUCCESS: SUCCESS,
  DANGER: DANGER,
  WARNING: WARNING,
});

Messenger.extend = extend;

export default Messenger;



//////////////////
// WEBPACK FOOTER
// ./public/js/core/base/Messenger.js
// module id = 662
// module chunks = 1